import React, { useRef, useState, useEffect } from "react";
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    PawPrint,
} from "lucide-react";

import CommonButton from "../CommonButton";
import { PetStatus } from "../../types/Request/AdminRequest";

interface PetCarouselSectionProp {
    title?: React.ReactNode;

    handleProductPage?: (product: any) => void;

    products: any[];

    onAddToCartClick?: (product: any) => void;

    onCategoryClick?: (category: any) => void;

    onViewAllClick?: () => void;
}

const PetCarouselSection: React.FC<PetCarouselSectionProp> = ({
    title = "Essentials for Happy Paws",
    handleProductPage,
    products = [],
    onAddToCartClick,
    onCategoryClick,
    onViewAllClick,
}) => {
    /*
    |--------------------------------------------------------------------------
    | STATUS HELPERS
    |--------------------------------------------------------------------------
    */

    const statusOptionsList = [
        {
            label: "Available",
            value: 1,
        },
        {
            label: "Reserved",
            value: 2,
        },
        {
            label: "Booked",
            value: 3,
        },
        {
            label: "Adopted",
            value: 4,
        },
    ];

    /**
     * Converts every possible API status format into
     * one consistent numeric status.
     *
     * Supported:
     * 1
     * "1"
     * "Available"
     * 2
     * "2"
     * "Reserved"
     * 3
     * "3"
     * "Sold"
     * "Booked"
     * 4
     * "4"
     * "Adopted"
     */
    const getStatusValue = (status: any): number => {
        if (
            status === null ||
            status === undefined ||
            status === ""
        ) {
            return 1;
        }

        // Already numeric
        if (typeof status === "number") {
            return status;
        }

        // Numeric string: "1", "2", "3", "4"
        const numericStatus = Number(status);

        if (!Number.isNaN(numericStatus)) {
            return numericStatus;
        }

        // String status
        const normalizedStatus = String(status)
            .trim()
            .toLowerCase();

        switch (normalizedStatus) {
            case "available":
                return 1;

            case "reserved":
                return 2;

            case "sold":
            case "booked":
                return 3;

            case "adopted":
                return 4;

            default:
                return 1;
        }
    };

    /**
     * Converts status value into display text.
     *
     * Sold should be displayed as "Booked".
     */
    const getStatusString = (status: any): string => {
        const statusValue = getStatusValue(status);

        switch (statusValue) {
            case 1:
                return "Available";

            case 2:
                return "Reserved";

            case 3:
                return "Booked";

            case 4:
                return "Adopted";

            default:
                return "Available";
        }
    };

    /*
    |--------------------------------------------------------------------------
    | CAROUSEL
    |--------------------------------------------------------------------------
    */

    const scrollRef = useRef<HTMLDivElement>(null);

    const [canScrollLeft, setCanScrollLeft] =
        useState(false);

    const [canScrollRight, setCanScrollRight] =
        useState(true);

    /*
    |--------------------------------------------------------------------------
    | IMAGE SLIDER
    |--------------------------------------------------------------------------
    */

    const [activeIndex, setActiveIndex] = useState<{
        [key: number]: number;
    }>({});

    const [hoveredIndex, setHoveredIndex] =
        useState<number | null>(null);

    /*
    |--------------------------------------------------------------------------
    | AUTO IMAGE SLIDER ON HOVER
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (hoveredIndex === null) {
            return;
        }

        const interval = setInterval(() => {
            setActiveIndex((prev) => {
                const updated = {
                    ...prev,
                };

                const product =
                    products[hoveredIndex];

                if (!product) {
                    return prev;
                }

                const images = [
                    product.imageUrl,
                    ...(product.subimage || []).map(
                        (image: any) => image.image
                    ),
                ].filter(Boolean);

                if (images.length <= 1) {
                    return prev;
                }

                const current =
                    updated[hoveredIndex] ?? 0;

                updated[hoveredIndex] =
                    (current + 1) % images.length;

                return updated;
            });
        }, 1500);

        return () => {
            clearInterval(interval);
        };
    }, [hoveredIndex, products]);

    /*
    |--------------------------------------------------------------------------
    | CAROUSEL SCROLL
    |--------------------------------------------------------------------------
    */

    const scroll = (
        direction: "left" | "right"
    ) => {
        if (!scrollRef.current) {
            return;
        }

        scrollRef.current.scrollBy({
            left:
                direction === "left"
                    ? -320
                    : 320,
            behavior: "smooth",
        });
    };

    /*
    |--------------------------------------------------------------------------
    | CAROUSEL SCROLL STATE
    |--------------------------------------------------------------------------
    */

    const handleScroll = () => {
        if (!scrollRef.current) {
            return;
        }

        const {
            scrollLeft,
            scrollWidth,
            clientWidth,
        } = scrollRef.current;

        setCanScrollLeft(
            scrollLeft > 0
        );

        setCanScrollRight(
            scrollLeft + clientWidth <
            scrollWidth - 10
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CARD CLICK
    |--------------------------------------------------------------------------
    */

    const handleCardClick = (
        product: any
    ) => {


        onAddToCartClick?.(product);
    };

    /*
    |--------------------------------------------------------------------------
    | VIEW ALL
    |--------------------------------------------------------------------------
    */

    const handleViewAll = () => {
        onViewAllClick?.();
    };

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <section className="relative overflow-hidden">
            <div className="relative z-10">

                {/* ============================================================
                    HEADER
                ============================================================ */}

                <div className="flex flex-col md:flex-row md:items-center md:justify-between relative gap-3 pb-3">

                    <h2 className="text-xl md:text-2xl lg:text-2xl font-bold text-iconColor text-left">
                        {title}
                    </h2>

                    {/* Desktop View All */}

                    {products.length > 4 && (
                        <div className="hidden md:flex ml-auto">
                            <CommonButton
                                text="View All"
                                icon={
                                    <ChevronRight
                                        size={16}
                                    />
                                }
                                iconPosition="right"
                                onClick={
                                    handleViewAll
                                }
                            />
                        </div>
                    )}
                </div>

                {/* ============================================================
                    CAROUSEL
                ============================================================ */}

                <div className="relative">

                    {/* ========================================================
                        LEFT CAROUSEL ARROW
                    ======================================================== */}

                    <div className="absolute left-1 top-1/2 -translate-y-1/2 z-40">

                        <CommonButton
                            icon={
                                <ChevronLeft
                                    size={25}
                                />
                            }
                            onClick={() => {
                                scroll("left");
                            }}
                            disabled={
                                !canScrollLeft
                            }
                            className="bg-white"
                        />

                    </div>

                    {/* ========================================================
                        RIGHT CAROUSEL ARROW
                    ======================================================== */}

                    <div className="absolute right-2 top-1/2 -translate-y-1/2 z-40">

                        <CommonButton
                            icon={
                                <ChevronRight
                                    size={25}
                                />
                            }
                            onClick={() => {
                                scroll("right");
                            }}
                            disabled={
                                !canScrollRight
                            }
                            className="bg-white"
                        />

                    </div>

                    {/* ========================================================
                        PRODUCTS
                    ======================================================== */}

                    <div
                        ref={scrollRef}
                        onScroll={
                            handleScroll
                        }
                        className="flex gap-6 overflow-x-auto [&::-webkit-scrollbar]:hidden px-2 pt-2"
                    >

                        {products.map(
                            (
                                product,
                                index
                            ) => {

                                /*
                                ------------------------------------------------
                                PET STATUS
                                ------------------------------------------------
                                */

                                const petStatus =
                                    getStatusValue(
                                        product?.petStatus ??
                                        product?.PetStatus ??
                                        PetStatus.Available
                                    );

                                const isAvailable =
                                    petStatus === 1;

                                const statusText =
                                    getStatusString(
                                        petStatus
                                    );

                                /*
                                ------------------------------------------------
                                IMAGES
                                ------------------------------------------------
                                */

                                const images = [
                                    product?.imageUrl ??
                                    product?.ImageUrl,

                                    ...(product?.subimage ||
                                        product?.Subimage ||
                                        []
                                    ).map(
                                        (image: any) =>
                                            image?.image ??
                                            image?.Image
                                    ),
                                ].filter(Boolean);

                                /*
                                ------------------------------------------------
                                CURRENT IMAGE
                                ------------------------------------------------
                                */

                                const currentIndex =
                                    activeIndex[
                                    index
                                    ] ?? 0;

                                const currentImage =
                                    images[
                                    currentIndex
                                    ] ||
                                    "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=500";

                                /*
                                ------------------------------------------------
                                PRICE
                                ------------------------------------------------
                                */

                                const salePrice =
                                    Number(
                                        product?.salePrice ??
                                        product?.SalePrice ??
                                        0
                                    );

                                const regularPrice =
                                    Number(
                                        product?.price ??
                                        product?.Price ??
                                        0
                                    );

                                const hasSalePrice =
                                    salePrice > 0 &&
                                    regularPrice > 0 &&
                                    salePrice <
                                    regularPrice;

                                /*
                                ------------------------------------------------
                                RETURN CARD
                                ------------------------------------------------
                                */

                                return (
                                    <div
                                        key={
                                            product?.id ??
                                            product?.Id ??
                                            index
                                        }

                                        /*
                                        ----------------------------------------
                                        HOVER
                                        ----------------------------------------
                                        */

                                        onMouseEnter={() =>
                                            setHoveredIndex(
                                                index
                                            )
                                        }

                                        onMouseLeave={() =>
                                            setHoveredIndex(
                                                null
                                            )
                                        }

                                        /*
                                        ----------------------------------------
                                        WHOLE CARD CLICK
                                        ----------------------------------------
                                        */

                                        onClick={() =>
                                            handleCardClick(
                                                product
                                            )
                                        }

                                        className={`
                                            group
                                            w-[220px]
                                            md:w-[240px]
                                            lg:w-[260px]
                                            flex-shrink-0
                                            rounded-2xl
                                            flex
                                            flex-col
                                            justify-between
                                            border
                                            border-gray-200
                                            bg-white
                                            transition-all
                                            duration-300
                                            ${isAvailable
                                                ? "hover:-translate-y-2 hover:shadow-xl cursor-pointer"
                                                : "grayscale-[60%] cursor-default"
                                            }
                                        `}
                                    >

                                        {/* =================================================
                                            IMAGE
                                        ================================================= */}

                                        <div className="relative h-[245px] md:h-[285px] rounded-t-2xl flex items-center justify-center overflow-hidden group">

                                            <img
                                                src={
                                                    currentImage
                                                }
                                                alt={
                                                    product?.name ??
                                                    product?.Name ??
                                                    "Pet"
                                                }
                                                className="h-full w-full object-cover transition-all duration-500"
                                            />

                                            {/* =================================================
                                                UNAVAILABLE STATUS OVERLAY
                                            ================================================= */}

                                            {!isAvailable && (
                                                <div className="absolute inset-0 bg-black/35 flex items-center justify-center z-10">

                                                    <span className="px-4 py-2 rounded-full bg-black/75 text-white text-sm font-bold uppercase tracking-wide">
                                                        {
                                                            statusText
                                                        }
                                                    </span>

                                                </div>
                                            )}

                                            {/* =================================================
                                                LEFT IMAGE ARROW
                                            ================================================= */}

                                            {images.length >
                                                1 && (
                                                    <button
                                                        type="button"

                                                        onClick={(
                                                            e
                                                        ) => {
                                                            e.stopPropagation();

                                                            setActiveIndex(
                                                                (
                                                                    prev
                                                                ) => {
                                                                    const current =
                                                                        prev[
                                                                        index
                                                                        ] ??
                                                                        0;

                                                                    const newIndex =
                                                                        current ===
                                                                            0
                                                                            ? images.length -
                                                                            1
                                                                            : current -
                                                                            1;

                                                                    return {
                                                                        ...prev,
                                                                        [index]:
                                                                            newIndex,
                                                                    };
                                                                }
                                                            );
                                                        }}

                                                        className="absolute left-1 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur p-1.5 rounded-full shadow opacity-0 group-hover:opacity-100 transition"
                                                    >
                                                        <ChevronLeft className="w-4 h-4 text-gray-800" />
                                                    </button>
                                                )}

                                            {/* =================================================
                                                RIGHT IMAGE ARROW
                                            ================================================= */}

                                            {images.length >
                                                1 && (
                                                    <button
                                                        type="button"

                                                        onClick={(
                                                            e
                                                        ) => {
                                                            e.stopPropagation();

                                                            setActiveIndex(
                                                                (
                                                                    prev
                                                                ) => {
                                                                    const current =
                                                                        prev[
                                                                        index
                                                                        ] ??
                                                                        0;

                                                                    const newIndex =
                                                                        current ===
                                                                            images.length -
                                                                            1
                                                                            ? 0
                                                                            : current +
                                                                            1;

                                                                    return {
                                                                        ...prev,
                                                                        [index]:
                                                                            newIndex,
                                                                    };
                                                                }
                                                            );
                                                        }}

                                                        className="absolute right-1 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur p-1.5 rounded-full shadow opacity-0 group-hover:opacity-100 transition"
                                                    >
                                                        <ChevronRight className="w-4 h-4 text-gray-800" />
                                                    </button>
                                                )}

                                        </div>

                                        {/* =================================================
                                            INFO
                                        ================================================= */}

                                        <div className="p-3 space-y-1">

                                            {/* =================================================
                                                NAME + GENDER
                                            ================================================= */}

                                            <div className="flex items-center justify-between">

                                                <p className="text-lg font-semibold text-logoHeading leading-tight truncate max-w-[130px]">
                                                    {
                                                        product?.name ??
                                                        product?.Name
                                                    }
                                                </p>

                                                <span className="text-[11px] font-medium text-gray-800 bg-accent/40 px-2 py-0.5 rounded-full">
                                                    {
                                                        product?.gender ??
                                                        product?.Gender ??
                                                        "Pet"
                                                    }
                                                </span>

                                            </div>

                                            {/* =================================================
                                                BADGES
                                            ================================================= */}

                                            <div className="flex items-center gap-2 flex-wrap py-2">

                                                <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">

                                                    <PawPrint
                                                        size={11}
                                                    />

                                                    {
                                                        product?.breed ??
                                                        product?.Breed
                                                    }

                                                </span>

                                                {(
                                                    product?.tag ??
                                                    product?.Tag
                                                ) && (
                                                        <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full">

                                                            <Calendar
                                                                size={11}
                                                            />

                                                            {
                                                                product?.tag ??
                                                                product?.Tag
                                                            }

                                                        </span>
                                                    )}

                                            </div>

                                            <div className="h-px bg-gray-100" />

                                            {/* =================================================
                                                PRICING
                                            ================================================= */}
{/* =================================================
    PRICING
================================================= */}

{Number(product?.petType ?? product?.PetType) !== 2 && (
    <div className="flex items-center gap-3 py-1">
        {hasSalePrice ? (
            <>
                <span className="text-lg font-semibold text-logoHeading">
                    AED {salePrice}
                </span>

                <span className="text-sm font-semibold text-gray-400 line-through">
                    AED {regularPrice}
                </span>
            </>
        ) : (
            regularPrice > 0 && (
                <span className="text-lg font-semibold text-logoHeading">
                    AED {regularPrice}
                </span>
            )
        )}
    </div>
)}

                                            {/* =================================================
                                                BOOK NOW / STATUS
                                            ================================================= */}

                                            <div className="pt-2">

                                                {isAvailable ? (
                                                    <CommonButton
                                                        text="Book Now"
                                                        variant="filled"
                                                        rounded="md"
                                                        className="w-full bg-mainBrandColor"
                                                        textContainerClassName="py-2"

                                                        onClick={(

                                                        ) => {


                                                            onAddToCartClick?.(
                                                                product
                                                            );
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full py-2 rounded-md bg-gray-200 text-gray-600 text-center font-semibold text-sm">
                                                        {
                                                            statusText
                                                        }
                                                    </div>
                                                )}

                                            </div>

                                        </div>

                                    </div>
                                );
                            }
                        )}

                    </div>

                    {/* ========================================================
                        MOBILE VIEW ALL
                    ======================================================== */}

                    <div className="flex items-center justify-center md:hidden pt-3">

                        <CommonButton
                            text="View All"
                            icon={
                                <ChevronRight
                                    size={16}
                                />
                            }
                            iconPosition="right"
                            onClick={
                                handleViewAll
                            }
                        />

                    </div>

                </div>
            </div>
        </section>
    );
};

export default PetCarouselSection;